import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaVideo, FaFilePdf, FaChevronLeft, FaPlay, FaDownload, FaShieldAlt } from 'react-icons/fa';
import DashboardLayout from '../../components/layout/DashboardLayout';
import API_BASE_URL from '../../config/api';

const CourseMaterials = () => {
  const { monthId } = useParams();
  const navigate = useNavigate();
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeVideo, setActiveVideo] = useState(null);
  const [security, setSecurity] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const user = JSON.parse(localStorage.getItem('user'));

  useEffect(() => {
    if (!user || !user.id) { navigate('/login'); return; }
    const fetchSettings = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/admin/manage_settings.php`);
        const result = await response.json();
        if (result.status === 'success') setSecurity(result.data);
      } catch (e) { console.error(e); }
    };
    fetchSettings();
  }, []);

  useEffect(() => {
    if (!user || !user.id) return;
    const fetchMaterials = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/get_materials.php?month_id=${monthId}&user_id=${user.id}`);
        const result = await response.json();
        if (result.status === 'success') {
          if (result.access === 'granted') {
            setMaterials(result.data);
            const firstVideo = result.data.find(m => m.type === 'video');
            if (firstVideo) setActiveVideo(firstVideo);
          } else {
            setAccessDenied(true);
          }
        }
      } catch (error) {
        console.error("Error fetching materials:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchMaterials();
  }, [monthId]);

  if (!user || !user.id) return null;

  return (
    <DashboardLayout title="Course Materials">
      <div className="space-y-4 sm:space-y-6">
        <button onClick={() => navigate('/student/courses')} className="flex items-center gap-1.5 text-slate-400 hover:text-orange-500 font-bold text-[10px] uppercase tracking-widest transition-colors">
          <FaChevronLeft size={10} /> Back to My Courses
        </button>

        {loading ? (
          <div className="flex justify-center py-10">
            <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : accessDenied ? (
          <div className="max-w-sm mx-auto py-10 text-center space-y-5">
            <div className="w-16 h-16 sm:w-20 sm:h-20 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto text-2xl sm:text-4xl text-slate-300 border border-slate-200">
              <FaShieldAlt className="animate-pulse" />
            </div>
            <div>
              <h2 className="text-base sm:text-2xl font-black text-slate-800">Class Locked 🔒</h2>
              <p className="text-slate-500 text-xs font-medium mt-1">Complete your payment to gain instant access.</p>
            </div>
            <button
              onClick={() => navigate(`/student/payments?month=${monthId}`)}
              className="px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white font-black rounded-xl shadow-lg transition-all active:scale-95 text-[10px] uppercase tracking-widest"
            >
              Enroll & Unlock Now
            </button>
          </div>
        ) : (
          <div className="flex flex-col lg:grid lg:grid-cols-3 gap-4 sm:gap-6">

            {/* Materials Sidebar — shown on top on mobile */}
            <div className="order-1 lg:order-2 space-y-4">
              <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
                <div className="p-3 sm:p-4 border-b border-slate-50 bg-slate-50/50">
                  <h3 className="font-black text-slate-800 text-[10px] sm:text-xs uppercase tracking-widest">Course Curriculum</h3>
                </div>
                <div className="divide-y divide-slate-50">
                  {[
                    { id: 'video', label: 'Video Lessons', icon: <FaVideo size={10} />, color: 'text-red-500', bg: 'bg-red-50' },
                    { id: 'live', label: 'Live Sessions', icon: <div className="w-2 h-2 bg-teal-500 rounded-full animate-ping" />, color: 'text-teal-500', bg: 'bg-teal-50' },
                    { id: 'pdf', label: 'PDF Documents', icon: <FaFilePdf size={10} />, color: 'text-blue-500', bg: 'bg-blue-50' },
                    { id: 'recording', label: 'Class Recordings', icon: <FaPlay size={10} />, color: 'text-purple-500', bg: 'bg-purple-50' }
                  ].map((group) => {
                    const groupItems = materials.filter(m => m.type === group.id);
                    if (groupItems.length === 0) return null;

                    return (
                      <div key={group.id} className="p-2 sm:p-3">
                        <div className="flex items-center gap-2 mb-2 px-1">
                          <span className={`text-[9px] font-black uppercase tracking-[0.2em] ${group.color}`}>{group.label}</span>
                          <div className="h-[1px] bg-slate-100 flex-grow"></div>
                        </div>
                        <div className="space-y-1">
                          {groupItems.map((mat) => (
                            <div
                              key={mat.id}
                              onClick={() => setActiveVideo(mat)}
                              className={`flex items-center gap-3 p-2.5 rounded-xl cursor-pointer transition-all ${
                                activeVideo?.id === mat.id ? 'bg-orange-50 border border-orange-100 shadow-sm' : 'hover:bg-slate-50 border border-transparent'
                              }`}
                            >
                              <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                                activeVideo?.id === mat.id ? 'bg-orange-500 text-white shadow-lg shadow-orange-200' : `${group.bg} ${group.color}`
                              }`}>
                                {mat.type === 'video' || mat.type === 'recording' ? <FaPlay size={8} /> :
                                 mat.type === 'pdf' ? <FaDownload size={8} /> : group.icon}
                              </div>
                              <div className="flex-grow min-w-0">
                                <h4 className={`text-[10px] sm:text-[11px] font-bold leading-tight truncate ${activeVideo?.id === mat.id ? 'text-orange-600' : 'text-slate-700'}`}>
                                  {mat.title}
                                </h4>
                                {mat.type === 'live' && mat.scheduled_at && (
                                  <p className="text-[7px] font-black text-teal-500 uppercase tracking-widest mt-0.5">
                                    {new Date(mat.scheduled_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                  </p>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                  {materials.length === 0 && (
                    <div className="p-10 text-center text-slate-400 text-[10px] font-bold italic">No materials uploaded for this session yet.</div>
                  )}
                </div>
              </div>

              <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl p-5 text-white shadow-lg relative overflow-hidden group">
                <div className="absolute top-0 right-0 w-24 h-24 bg-orange-500 opacity-10 rounded-full -mr-12 -mt-12 group-hover:scale-150 transition-transform duration-700"></div>
                <h4 className="font-black text-xs mb-1 relative z-10">Need Technical Help?</h4>
                <p className="text-[9px] font-medium text-slate-400 mb-4 leading-relaxed relative z-10">If you're having trouble accessing videos or downloading PDFs, please contact our support team.</p>
                <button className="w-full py-2.5 bg-orange-500 hover:bg-orange-600 rounded-lg text-[9px] font-black uppercase tracking-widest transition-all shadow-lg shadow-orange-900/20 relative z-10">
                  Contact Support
                </button>
              </div>
            </div>

            {/* Main Video Player */}
            <div className="order-2 lg:order-1 lg:col-span-2 space-y-4">
              {activeVideo && (activeVideo.type === 'video' || activeVideo.type === 'recording') ? (
                <div className="bg-black rounded-xl sm:rounded-2xl overflow-hidden shadow-2xl aspect-video relative">
                  {security?.enable_watermark === '1' && security?.master_security_mode === '1' && (
                    <div className="absolute inset-0 z-10 pointer-events-none select-none flex items-center justify-center overflow-hidden">
                      <div className="text-white/20 font-black text-2xl sm:text-4xl transform -rotate-45 whitespace-nowrap">
                        {user?.student_id} | {user?.full_name}
                      </div>
                    </div>
                  )}
                  <iframe
                    className="w-full h-full"
                    src={activeVideo.url.includes('youtube.com') ? activeVideo.url.replace('watch?v=', 'embed/') : activeVideo.url}
                    title={activeVideo.title}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>
              ) : activeVideo && activeVideo.type === 'pdf' ? (
                <div className="bg-slate-100 rounded-xl sm:rounded-2xl overflow-hidden shadow-2xl h-[500px] sm:h-[700px] relative border border-slate-200">
                  <iframe
                    className="w-full h-full"
                    src={activeVideo.url}
                    title={activeVideo.title}
                  ></iframe>
                </div>
              ) : activeVideo && activeVideo.type === 'live' ? (
                <div className="bg-slate-900 rounded-xl sm:rounded-2xl aspect-video flex flex-col items-center justify-center text-white gap-4 border border-slate-800 shadow-2xl">
                  <div className="w-14 h-14 sm:w-20 sm:h-20 bg-teal-500/20 rounded-full flex items-center justify-center border border-teal-500/30">
                    <div className="w-3 h-3 sm:w-4 sm:h-4 bg-teal-500 rounded-full animate-ping"></div>
                  </div>
                  <div className="text-center px-4">
                    <h2 className="text-sm sm:text-xl font-black uppercase tracking-tight">Live Class Session</h2>
                    <p className="text-slate-400 text-[10px] font-medium mt-1">{new Date(activeVideo.scheduled_at).toLocaleString()}</p>
                  </div>
                  <button onClick={() => window.open(activeVideo.url)} className="px-6 py-3 bg-teal-500 hover:bg-teal-600 text-white font-black rounded-xl shadow-xl transition-all active:scale-95 flex items-center gap-2 text-[10px] uppercase tracking-widest">
                    <FaPlay size={10} /> Join Live Stream
                  </button>
                </div>
              ) : (
                <div className="bg-slate-100 rounded-xl sm:rounded-2xl aspect-video flex flex-col items-center justify-center text-slate-400 gap-3 border-2 border-dashed border-slate-200">
                  <FaVideo size={32} />
                  <p className="font-bold text-xs">Select a lesson to start learning</p>
                </div>
              )}

              <div className="bg-white p-4 sm:p-6 rounded-xl border border-slate-100 shadow-sm">
                <div className="flex justify-between items-start gap-4">
                  <div>
                    <h2 className="text-sm sm:text-lg font-black text-slate-800 mb-1">{activeVideo?.title || 'Class Content'}</h2>
                    <p className="text-[10px] sm:text-xs text-slate-500 font-medium leading-relaxed whitespace-pre-wrap">
                      {activeVideo?.description || "Welcome to this month's lesson. Watch carefully and download PDF materials for your studies."}
                    </p>
                  </div>
                  {activeVideo?.type === 'pdf' && (
                    <button onClick={() => window.open(activeVideo.url)} className="flex-shrink-0 px-3 py-2 bg-blue-50 hover:bg-blue-100 text-blue-600 rounded-lg text-[9px] font-black uppercase tracking-widest flex items-center gap-1.5 transition-colors">
                      <FaDownload size={10} /> Download
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default CourseMaterials;
